import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ordersApi, factoriesApi, saleshubApi, customersApi } from '../services/api';
import ChainBreakdownPicker from '../components/ChainBreakdownPicker';
import FactoryConcernsPanel from '../components/FactoryConcernsPanel';
import WhatsAppRecipientPicker from '../components/WhatsAppRecipientPicker';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/layout/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { format, addDays } from 'date-fns';
import { toast } from 'sonner';
import { 
  Plus, Search, Filter, Package, Calendar as CalendarIcon, Loader2, Eye, Trash2, 
  AlertCircle, CheckCircle2, Factory, ArrowUp, ArrowDown, ArrowUpDown, Printer,
  Layers, ArrowRight, Archive, ArchiveRestore, Download, ChevronsUpDown, Check,
  Gem, Link2, AlertTriangle, ChevronRight, ChevronDown, IndianRupee, Scale, Boxes
} from 'lucide-react';
import { formatDate, getDaysUntil, getActiveDaysUntil, isOrderDispatchedOrClosed, getPriorityClass, getProgressPercentage, cn } from '../lib/utils';

const formatCurrency = (v) => {
  if (!v) return '0';
  return Number(v).toLocaleString('en-IN');
};

const DIAMOND_QUALITIES = ["VVS EF", "GH SI", "VSSI GH", "SI-IJ", "VVSVS GH", "VVS FG"];
const CERTIFICATION_LABS = ["IGI", "SGL", "DHC", "IDT", "Other"];
const GOLD_KARATS = ["9kt", "14kt", "18kt"];
const PRIORITIES = ["Normal", "High", "Urgent"];
const DEFAULT_DELIVERY_DAYS = 28;

// --- Factory acceptance status badge ---
// Renders next to the order number on the Admin Orders page so the team can
// see at a glance which factories haven't acknowledged a new brief yet.
const FactoryAcceptanceBadge = ({ order, compact = false }) => {
  const fa = order.factory_acceptance;
  if (!fa || fa.status === 'accepted') return null;
  if (fa.status === 'pending') {
    // Compute "awaiting since" — used to nudge ops if a factory is sitting on a brief.
    let suffix = '';
    if (fa.requested_at) {
      const hours = Math.max(0, Math.round((Date.now() - new Date(fa.requested_at).getTime()) / 3600000));
      suffix = hours < 1 ? ' • just now'
        : hours < 24 ? ` • ${hours}h`
        : ` • ${Math.floor(hours / 24)}d`;
    }
    return (
      <Badge
        className={`bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-500/20 dark:text-amber-300 dark:border-amber-500/40 ${compact ? 'text-[9px] h-4 px-1.5' : 'text-[10px]'}`}
        title={`Awaiting factory acceptance${fa.requested_at ? ` since ${new Date(fa.requested_at).toLocaleString('en-IN')}` : ''}`}
        data-testid={`order-pending-acceptance-${order.id}`}
      >
        {compact ? `Awaiting${suffix}` : `Awaiting acceptance${suffix}`}
      </Badge>
    );
  }
  if (fa.status === 'rejected') {
    return (
      <Badge
        variant="destructive"
        className={`${compact ? 'text-[9px] h-4 px-1.5' : 'text-[10px]'}`}
        title={fa.reject_reason ? `Reject reason: ${fa.reject_reason}` : 'Factory rejected — re-send the order'}
        data-testid={`order-rejected-${order.id}`}
      >
        Factory rejected
      </Badge>
    );
  }
  return null;
};

// --- Manufacturing Order Form + Factory Processing Sheet status badge ---
// Both files must be uploaded before the factory can see the order.
// Green pill = both uploaded; rose pill = one or both missing.
const ManufacturingFormBadge = ({ order, compact = false }) => {
  if (!order.factory_id) return null;
  const piecesToFactory = order.pieces_to_factory ?? (order.pieces_ordered - (order.ready_from_inventory || 0));
  if (piecesToFactory <= 0) return null;
  const hasMfg = !!order.manufacturing_form;
  const hasSheet = !!order.factory_processing_sheet;
  if (hasMfg && hasSheet) {
    return (
      <Badge
        className={`bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/40 ${compact ? 'text-[9px] h-4 px-1.5' : 'text-[10px]'}`}
        title="Manufacturing Order Form and Factory Processing Sheet both uploaded"
        data-testid={`order-files-ready-${order.id}`}
      >
        {compact ? 'Files ✓' : 'Files ready ✓'}
      </Badge>
    );
  }
  const missing = [!hasMfg && 'MFG form', !hasSheet && 'Processing sheet'].filter(Boolean).join(' + ');
  return (
    <Badge
      className={`bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-500/20 dark:text-rose-300 dark:border-rose-500/40 ${compact ? 'text-[9px] h-4 px-1.5' : 'text-[10px]'}`}
      title={`Missing: ${missing} — order is hidden from the factory until both files are uploaded.`}
      data-testid={`order-files-missing-${order.id}`}
    >
      {compact ? 'Files needed' : `Missing ${missing}`}
    </Badge>
  );
};


// --- Status/Bottleneck display: shows pieces at each active stage ---
const StageBreakdown = ({ stageQuantities, status, factorySubStages, dispatches }) => {
  if (status === 'Dispatched') {
    const allApprovalOnly = (dispatches || []).length > 0 && (dispatches || []).every(d => !d.invoiced && d.dispatch_type !== 'invoice');
    if (allApprovalOnly) {
      return (
        <span className="flex items-center gap-1 text-red-600 dark:text-red-400 text-xs font-semibold" data-testid="dispatched-on-approval-label">
          <AlertCircle className="h-3 w-3" /> Dispatched on Approval
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-xs font-medium">
        <CheckCircle2 className="h-3 w-3" /> Dispatched
      </span>
    );
  }
  const active = (stageQuantities || []).filter(sq => sq.quantity > 0);
  if (active.length === 0) return <span className="text-xs text-muted-foreground">{status}</span>;

  const activeSubs = (factorySubStages || []).filter(s => s.quantity > 0);

  const partialApprovalOnly = status === 'Partially Dispatched' && (dispatches || []).length > 0 && (dispatches || []).every(d => !d.invoiced && d.dispatch_type !== 'invoice');

  return (
    <div>
      <div className="flex flex-wrap gap-x-1 gap-y-0.5 text-xs leading-relaxed">
        {active.map((sq, i) => (
          <span key={sq.stage}>
            <span className="font-semibold text-foreground">{sq.quantity}</span>
            <span className="text-muted-foreground"> pcs in {sq.stage}</span>
            {i < active.length - 1 && <span className="text-muted-foreground/50 mx-0.5">/</span>}
          </span>
        ))}
      </div>
      {active.some(sq => sq.stage === 'On the Floor') && activeSubs.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-0.5">
          {activeSubs.map(sub => (
            <span key={sub.stage} className="text-[9px] px-1 py-0 rounded bg-blue-100/70 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400">
              {sub.quantity} {sub.stage}
            </span>
          ))}
        </div>
      )}
      {partialApprovalOnly && (
        <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-red-600 dark:text-red-400" data-testid="partial-dispatched-on-approval-label">
          <AlertCircle className="h-3 w-3" /> Dispatched on Approval
        </span>
      )}
    </div>
  );
};

const MATERIAL_TOLERANCE = 0.02;

// --- Material cell for a single material type ---
const PENDING_COLORS = {
  gold: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400 hover:ring-red-400",
  diamond: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400 hover:ring-amber-400",
  chain: "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400 hover:ring-violet-400",
};

const MaterialCell = ({ material, type, unit, orderId, navigate }) => {
  const req = material.total_required || material.required || material.weight || material.quantity_required || 0;
  const iss = material.total_issued || 0;
  if (req === 0) return <span className="text-[10px] text-muted-foreground">—</span>;
  const done = iss >= req || Math.abs(req - iss) <= MATERIAL_TOLERANCE;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); navigate(`/orders/${orderId}?tab=materials`); }}
      className={cn(
        "text-[10px] px-1.5 py-0.5 rounded font-semibold cursor-pointer transition-all hover:ring-1",
        done
          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400 hover:ring-emerald-400"
          : PENDING_COLORS[type] || "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400 hover:ring-amber-400"
      )}
      data-testid={`material-${type}-${orderId}`}
    >
      {unit ? `${parseFloat(iss).toFixed(2)}/${parseFloat(req).toFixed(2)}` : `${iss}/${req}`}{unit}
    </button>
  );
};

// --- Sticker status button ---
const StickerButton = ({ order, onConfirm }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Only show for orders that have pieces in "Order Raised"
  const hasOrderRaised = (order.stage_quantities || []).some(
    sq => sq.stage === 'Order Raised' && sq.quantity > 0
  );

  const showButton = !order.sticker_printed && hasOrderRaised;

  return (
    <>
      {order.sticker_printed ? (
        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30 text-[10px]" data-testid={`sticker-done-${order.id}`}>
          <CheckCircle2 className="h-3 w-3 mr-1" /> Done
        </Badge>
      ) : !hasOrderRaised ? (
        <span className="text-[10px] text-muted-foreground">N/A</span>
      ) : (
        <button
          onClick={(e) => { e.stopPropagation(); setDialogOpen(true); }}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-400 border border-cyan-300 dark:border-cyan-500/40 animate-pulse hover:animate-none transition-all cursor-pointer"
          data-testid={`sticker-pending-${order.id}`}
        >
          <Printer className="h-3 w-3" /> Pending
        </button>
      )}
      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!confirming) setDialogOpen(v); }}>
        <DialogContent className="max-w-sm" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="font-heading">Sticker Print Confirmation</DialogTitle>
            <DialogDescription>
              Order <strong>{order.order_number}</strong> — Has the sticker been printed for this order?
            </DialogDescription>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Pieces cannot be moved out of "Order Raised" stage until the sticker is marked as printed.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={confirming}>
              Not Yet
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={confirming}
              data-testid={`sticker-confirm-${order.id}`}
              onClick={async () => {
                setConfirming(true);
                setDialogOpen(false);
                await onConfirm(order.id);
                setConfirming(false);
              }}
            >
              {confirming ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Yes, Printed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// --- Gold Rate Cut button ---
const GoldRateCutButton = ({ order, onSave, userRole }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rateType, setRateType] = useState('995_gst');
  const [rateTypeOther, setRateTypeOther] = useState('');
  const [goldRate, setGoldRate] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);

  const hasRateCut = !!order.gold_rate_cut?.rate_type;
  const canEdit = !hasRateCut || userRole === 'super_admin';
  const isReadyToShip = (order.stage_quantities || []).some(sq => sq.stage === 'Ready to Ship' && sq.quantity > 0) ||
    order.status === 'Ready to Ship' || order.status === 'Dispatched' || order.status === 'Partially Dispatched';

  if (!isReadyToShip && !hasRateCut) return null;

  const handleSave = async () => {
    if (!goldRate || parseFloat(goldRate) <= 0) { toast.error('Enter a valid gold rate'); return; }
    if (rateType === 'other' && !rateTypeOther.trim()) { toast.error('Specify rate type'); return; }
    setSaving(true);
    try {
      await onSave(order.id, { rate_type: rateType, rate_type_other: rateTypeOther, gold_rate_per_10g: parseFloat(goldRate), notes }, file);
      setDialogOpen(false);
      toast.success('Gold rate cut saved');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to save';
      toast.error(msg);
    }
    finally { setSaving(false); }
  };

  const typeLabel = (t, other) => {
    if (t === '995_gst') return '995+GST';
    if (t === '999_gst') return '999+GST';
    return other || 'Other';
  };

  const openDialog = (e) => {
    e.stopPropagation();
    if (hasRateCut && !canEdit) { toast.error('Only Super Admin can edit rate cut'); return; }
    if (hasRateCut) {
      setRateType(order.gold_rate_cut.rate_type);
      setRateTypeOther(order.gold_rate_cut.rate_type_other || '');
      setGoldRate(String(order.gold_rate_cut.gold_rate_per_10g));
      setNotes(order.gold_rate_cut.notes || '');
    } else {
      setRateType('995_gst'); setGoldRate(''); setNotes(''); setFile(null);
    }
    setDialogOpen(true);
  };

  return (
    <>
      {hasRateCut ? (
        <Badge
          className={`bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400 border-amber-200 dark:border-amber-500/30 text-[10px] transition-colors ${canEdit ? 'cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-500/25' : ''}`}
          variant="outline"
          onClick={canEdit ? openDialog : undefined}
          data-testid={`rate-cut-done-${order.id}`}
        >
          <IndianRupee className="h-3 w-3 mr-0.5" />
          {formatCurrency(order.gold_rate_cut.gold_rate_per_10g)}
          <span className="ml-0.5 opacity-70">({typeLabel(order.gold_rate_cut.rate_type, order.gold_rate_cut.rate_type_other)})</span>
        </Badge>
      ) : (
        <button
          onClick={openDialog}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400 border border-amber-300 dark:border-amber-500/40 hover:bg-amber-200 dark:hover:bg-amber-500/30 transition-all cursor-pointer"
          data-testid={`rate-cut-pending-${order.id}`}
        >
          <IndianRupee className="h-3 w-3" /> Rate Cut
        </button>
      )}
      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!saving) setDialogOpen(v); }}>
        <DialogContent className="max-w-sm" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <IndianRupee className="h-5 w-5 text-amber-500" />
              Gold Rate Cut — {order.order_number}
            </DialogTitle>
            <DialogDescription>Record the gold rate cut before dispatch</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Rate Type *</Label>
              <Select value={rateType} onValueChange={setRateType}>
                <SelectTrigger data-testid="rate-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="995_gst">995 with GST</SelectItem>
                  <SelectItem value="999_gst">999 with GST</SelectItem>
                  <SelectItem value="other">Other (specify)</SelectItem>
                </SelectContent>
              </Select>
              {rateType === 'other' && (
                <Input placeholder="Specify rate type..." value={rateTypeOther} onChange={(e) => setRateTypeOther(e.target.value)} className="mt-2" data-testid="rate-type-other-input" />
              )}
            </div>
            <div className="space-y-2">
              <Label>Gold Rate per 10gm (Rs) *</Label>
              <Input type="number" min="0" step="1" value={goldRate} onChange={(e) => setGoldRate(e.target.value)} placeholder="e.g. 72500" data-testid="gold-rate-input" />
            </div>
            <div className="space-y-2">
              <Label>Upload Rate Cut Screenshot</Label>
              <Input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} data-testid="rate-cut-file-input" />
              {(order.gold_rate_cut?.screenshot_url || order.gold_rate_cut?.screenshot_path) && !file && (
                <a href={order.gold_rate_cut.screenshot_url || ordersApi.getGoldRateScreenshotUrl(order.id)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline">View existing screenshot</a>
              )}
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any notes..." data-testid="rate-cut-notes-input" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="save-rate-cut-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <IndianRupee className="h-4 w-4 mr-1" />}
              {hasRateCut ? 'Update' : 'Save'} Rate Cut
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// --- Hover Card for order piece details ---
const DesignDepChip = ({ orderId, summary }) => {
  if (!summary || summary.total === 0) return null;
  const ready = summary.state === 'ready_for_production';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full border',
        ready
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30'
          : 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/30'
      )}
      data-testid={`design-dep-chip-${orderId}`}
      title={ready ? 'All design dependencies complete' : `${summary.completed}/${summary.total} design tasks done`}
    >
      {ready ? 'Design ✓' : `Design ${summary.completed}/${summary.total}`}
    </span>
  );
};



// --- Material Issuance Alerts Banner ---
const MaterialAlertsBanner = ({ orders, navigate }) => {
  const [expanded, setExpanded] = useState(false);

  const pendingGold = useMemo(() => orders.filter(o => {
    if (o.status === 'Dispatched' || o.archived) return false;
    const gold = o.materials?.gold;
    const req = gold?.total_required || gold?.required || o.order_gold_weight || 0;
    if (req <= 0) return false;
    return !gold?.issued_status;
  }).map(o => ({
    id: o.id, order_number: o.order_number, customer_name: o.customer_name,
    issued: o.materials?.gold?.total_issued || 0,
    required: o.materials?.gold?.total_required || o.materials?.gold?.required || o.order_gold_weight || 0,
  })), [orders]);

  const pendingDiamonds = useMemo(() => orders.filter(o => {
    if (o.status === 'Dispatched' || o.archived) return false;
    const dia = o.materials?.diamonds;
    const req = dia?.total_required || dia?.weight || o.order_diamond_weight || 0;
    if (req <= 0) return false;
    return !(dia?.issued_status || dia?.issued);
  }).map(o => ({
    id: o.id, order_number: o.order_number, customer_name: o.customer_name,
    issued: o.materials?.diamonds?.total_issued || 0,
    required: o.materials?.diamonds?.total_required || o.materials?.diamonds?.weight || o.order_diamond_weight || 0,
  })), [orders]);

  const pendingChains = useMemo(() => orders.filter(o => {
    if (o.status === 'Dispatched' || o.archived) return false;
    const ch = o.materials?.chains;
    const req = ch?.total_required || ch?.quantity_required || 0;
    if (req <= 0) return false;
    return !ch?.issued_status;
  }).map(o => ({
    id: o.id, order_number: o.order_number, customer_name: o.customer_name,
    issued: o.materials?.chains?.total_issued || 0,
    required: o.materials?.chains?.total_required || o.materials?.chains?.quantity_required || 0,
  })), [orders]);

  const goldCount = pendingGold.length;
  const diamondCount = pendingDiamonds.length;
  const chainCount = pendingChains.length;
  if (goldCount === 0 && diamondCount === 0 && chainCount === 0) return null;

  return (
    <div className="mb-4 md:mb-6" data-testid="material-alerts-banner">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg bg-gradient-to-r from-red-50 to-amber-50 dark:from-red-500/10 dark:to-amber-500/10 border border-red-200/60 dark:border-red-500/20 hover:border-red-300 dark:hover:border-red-500/30 transition-all"
        data-testid="material-alerts-toggle"
      >
        <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
        <span className="text-sm font-semibold text-foreground">Material Pending</span>
        <div className="flex gap-2 ml-1">
          {goldCount > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 text-[11px] font-bold">
              {goldCount} Gold
            </span>
          )}
          {diamondCount > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 text-[11px] font-bold">
              {diamondCount} Diamond
            </span>
          )}
          {chainCount > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-400 text-[11px] font-bold">
              {chainCount} Chain
            </span>
          )}
        </div>
        <div className="ml-auto flex-shrink-0">
          {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </div>
      </button>

      {expanded && (
        <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3">
          {goldCount > 0 && (
            <Card className="border-red-200 dark:border-red-500/20">
              <CardContent className="p-3">
                <p className="text-xs font-bold text-red-600 dark:text-red-400 mb-2 flex items-center gap-1.5">
                  <Gem className="h-3 w-3" /> Gold Not Issued ({goldCount})
                </p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {pendingGold.map(o => (
                    <div
                      key={o.id}
                      onClick={() => navigate(`/orders/${o.id}?tab=materials`)}
                      className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-red-50 dark:hover:bg-red-500/10 cursor-pointer transition-colors"
                      data-testid={`gold-pending-${o.id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-xs font-semibold truncate">{o.order_number}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{o.customer_name}</p>
                      </div>
                      <span className="text-[10px] font-bold text-red-600 dark:text-red-400 flex-shrink-0 ml-2 tabular-nums">
                        {o.issued > 0 ? `${parseFloat(o.issued).toFixed(2)}/${parseFloat(o.required).toFixed(2)}g` : 'Not issued'}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {diamondCount > 0 && (
            <Card className="border-amber-200 dark:border-amber-500/20">
              <CardContent className="p-3">
                <p className="text-xs font-bold text-amber-600 dark:text-amber-400 mb-2 flex items-center gap-1.5">
                  <Gem className="h-3 w-3" /> Diamonds Not Issued ({diamondCount})
                </p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {pendingDiamonds.map(o => (
                    <div
                      key={o.id}
                      onClick={() => navigate(`/orders/${o.id}?tab=materials`)}
                      className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-amber-50 dark:hover:bg-amber-500/10 cursor-pointer transition-colors"
                      data-testid={`diamond-pending-${o.id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-xs font-semibold truncate">{o.order_number}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{o.customer_name}</p>
                      </div>
                      <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 flex-shrink-0 ml-2 tabular-nums">
                        {o.issued > 0 ? `${parseFloat(o.issued).toFixed(2)}/${parseFloat(o.required).toFixed(2)}ct` : 'Not issued'}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {chainCount > 0 && (
            <Card className="border-violet-200 dark:border-violet-500/20">
              <CardContent className="p-3">
                <p className="text-xs font-bold text-violet-600 dark:text-violet-400 mb-2 flex items-center gap-1.5">
                  <Link2 className="h-3 w-3" /> Chains Not Issued ({chainCount})
                </p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {pendingChains.map(o => (
                    <div
                      key={o.id}
                      onClick={() => navigate(`/orders/${o.id}?tab=materials`)}
                      className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-violet-50 dark:hover:bg-violet-500/10 cursor-pointer transition-colors"
                      data-testid={`chain-pending-${o.id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-xs font-semibold truncate">{o.order_number}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{o.customer_name}</p>
                      </div>
                      <span className="text-[10px] font-bold text-violet-600 dark:text-violet-400 flex-shrink-0 ml-2 tabular-nums">
                        {o.issued > 0 ? `${o.issued}/${o.required}` : 'Not issued'}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

// --- WIP Summary ---
// Shows the global "Work In Progress" count plus a per-factory breakdown.
// WIP = Order Raised (accepted only) + Casting + On the Floor pieces, summed
// across active orders. Matches the factory-portal WIP definition exactly so
// the same number lands on both admin Orders and factory dashboards.
// Pending-acceptance and rejected orders are excluded.
const WIPSummary = ({ orders, factories }) => {
  const { globalWip, perFactory } = useMemo(() => {
    const stageQty = (o, stage) =>
      (o.stage_quantities || []).find(s => s.stage === stage)?.quantity || 0;
    const map = {};
    let total = 0;
    orders.forEach(o => {
      if (o.status === 'Dispatched' || o.archived) return;
      // Only inventory-only orders (no factory work) are excluded — they have no factory_id.
      if (!o.factory_id) return;
      const accStatus = o.factory_acceptance?.status;
      if (accStatus === 'pending' || accStatus === 'rejected') return;
      const wip = stageQty(o, 'Order Raised')
                + stageQty(o, 'Casting')
                + stageQty(o, 'On the Floor');
      if (wip <= 0) return;
      total += wip;
      const fId = o.factory_id || '__none__';
      const fName = o.factory_name || 'Unassigned';
      if (!map[fId]) map[fId] = { id: fId, name: fName, wip: 0, orders: 0 };
      map[fId].wip += wip;
      map[fId].orders += 1;
    });
    return {
      globalWip: total,
      perFactory: Object.values(map).sort((a, b) => b.wip - a.wip),
    };
  }, [orders]);

  if (globalWip === 0) return null;

  return (
    <Card className="mb-4 md:mb-6 border-blue-200 dark:border-blue-500/20" data-testid="wip-summary">
      <CardContent className="p-3 md:p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
          <div className="flex items-center gap-3 md:flex-shrink-0">
            <div className="h-10 w-10 rounded-xl bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center">
              <Scale className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Work In Progress</p>
              <p className="text-2xl font-bold tabular-nums" data-testid="wip-global-value">
                {globalWip}
                <span className="text-sm text-muted-foreground font-medium ml-1">pcs</span>
              </p>
            </div>
          </div>

          {perFactory.length > 0 && (
            <div className="flex-1 min-w-0 md:border-l md:pl-4 md:dark:border-slate-700">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
                By factory
              </p>
              <div className="flex flex-wrap gap-1.5">
                {perFactory.map(f => (
                  <Badge
                    key={f.id}
                    variant="outline"
                    className="text-[11px] py-1 px-2 bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30 hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-colors"
                    data-testid={`wip-chip-${f.id}`}
                    title={`${f.orders} active order(s) • ${f.wip} pcs pending return`}
                  >
                    <span className="font-semibold text-slate-700 dark:text-slate-200">{f.name}</span>
                    <span className="mx-1 text-slate-400">·</span>
                    <span className="font-bold tabular-nums text-blue-700 dark:text-blue-300">{f.wip}</span>
                    <span className="ml-0.5 text-[10px] text-slate-500">pcs</span>
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const Orders = () => {
  const navigate = useNavigate();
  const { isProduction, isAdmin, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [designSummary, setDesignSummary] = useState({}); // {order_id: {state, total, completed, pending, overdue}}
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [useCustomDeliveryDate, setUseCustomDeliveryDate] = useState(false);
  const [dateSortDir, setDateSortDir] = useState('desc'); // 'asc' | 'desc'
  const [sortMode, setSortMode] = useState('date'); // 'date' | 'custom'
  const [reordering, setReordering] = useState(null); // order id being reordered
  const [showArchived, setShowArchived] = useState(false);
  // 'all' (excludes stock by default), 'stock', or 'include' (everything)
  const [stockView, setStockView] = useState('all');
  const [saleshubCustomers, setSaleshubCustomers] = useState([]);
  const [approvedCustomers, setApprovedCustomers] = useState([]);
  const [customerComboOpen, setCustomerComboOpen] = useState(false);

  // New order form
  const [newOrder, setNewOrder] = useState({
    customer_id: '', customer_name: '', customer_phone: '', branch: '',
    notification_phones: [], // [{phone, name}]
    salesperson: '', factory_id: '', pieces_ordered: '',
    ready_from_inventory: '', co_number: '', diamond_quality: '', gold_karat: '18kt',
    certification_lab: '', certification_lab_other: '', cobranding: false,
    inventory_gold_weight: '', inventory_diamond_weight: '',
    order_gold_weight: '', order_diamond_weight: '', chains_pieces_factory: '',
    chain_breakdown: [],
    priority: 'Normal', notes: '',
    is_stock_order: false,
  });

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (isCreateOpen) {
      if (saleshubCustomers.length === 0) {
        saleshubApi.getCustomers().then(res => setSaleshubCustomers(res.data.customers || [])).catch(() => {});
      }
      if (approvedCustomers.length === 0) {
        customersApi.getApprovedForOrder()
          .then(res => setApprovedCustomers(res.data || []))
          .catch(() => toast.error('Could not load approved customers'));
      }
    }
  }, [isCreateOpen]);

  // Helpers
  const handleSelectCustomer = (cust) => {
    if (!cust) return;
    setNewOrder(prev => ({
      ...prev,
      customer_id: cust.id,
      customer_name: cust.company_name || '',
      customer_phone: cust.mobile || '',
      // Pre-select primary phone for WhatsApp updates
      notification_phones: cust.mobile ? [{
        phone: cust.mobile,
        name: cust.contact_person_name || cust.company_name || '',
      }] : [],
    }));
    setCustomerComboOpen(false);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, factoriesRes] = await Promise.all([
        ordersApi.getAll(), factoriesApi.getAll()
      ]);
      setOrders(ordersRes.data);
      setFactories(factoriesRes.data);
      // Best-effort fetch of design dep summary so we can show chips
      try {
        const { data: ds } = await axios.get(`${API}/design-dependencies/order-summary`);
        setDesignSummary(ds || {});
      } catch { /* ignore */ }
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrder = async () => {
    const piecesOrderedInt = parseInt(newOrder.pieces_ordered) || 0;
    const readyFromInventoryInt = parseInt(newOrder.ready_from_inventory) || 0;
    const piecesToFactory = piecesOrderedInt - readyFromInventoryInt;
    const isInventoryOnly = piecesToFactory <= 0;

    // Factory is required ONLY when at least one piece is going to the factory.
    // Fully-inventory orders skip the factory entirely — they shouldn't even appear on the factory portal.
    if (!newOrder.customer_name || !newOrder.pieces_ordered || !newOrder.co_number?.trim()) {
      toast.error('Please fill in all required fields (including CO Number)');
      return;
    }
    if (!isInventoryOnly && !newOrder.factory_id) {
      toast.error('Please select a factory for the pieces going to production');
      return;
    }
    if (newOrder.certification_lab === 'Other' && !newOrder.certification_lab_other.trim()) {
      toast.error('Please enter the certification lab name');
      return;
    }
    const piecesOrdered = piecesOrderedInt;
    const readyFromInventory = readyFromInventoryInt;
    if (readyFromInventory > piecesOrdered) {
      toast.error('Ready from inventory cannot exceed total pieces ordered');
      return;
    }
    if (useCustomDeliveryDate && !deliveryDate) {
      toast.error('Please select a delivery date or use default');
      return;
    }

    setCreating(true);
    try {
      await ordersApi.create({
        ...newOrder,
        // Inventory-only orders bypass the factory portal completely.
        factory_id: isInventoryOnly ? null : newOrder.factory_id,
        pieces_ordered: piecesOrdered,
        ready_from_inventory: readyFromInventory,
        inventory_gold_weight: parseFloat(newOrder.inventory_gold_weight) || 0,
        inventory_diamond_weight: parseFloat(newOrder.inventory_diamond_weight) || 0,
        order_gold_weight: parseFloat(newOrder.order_gold_weight) || 0,
        order_diamond_weight: parseFloat(newOrder.order_diamond_weight) || 0,
        chains_pieces_inventory: 0,
        chains_pieces_factory: parseInt(newOrder.chains_pieces_factory) || 0,
        chain_breakdown: (newOrder.chain_breakdown || [])
          .filter(b => b.chain_sku_id && parseInt(b.quantity) > 0)
          .map(b => ({ ...b, quantity: parseInt(b.quantity) })),
        delivery_date: useCustomDeliveryDate && deliveryDate ? deliveryDate.toISOString() : null,
        custom_delivery_date: useCustomDeliveryDate
      });
      toast.success('Order created successfully');
      setIsCreateOpen(false);
      setNewOrder({
        customer_id: '', customer_name: '', customer_phone: '', branch: '',
        notification_phones: [],
        salesperson: '', factory_id: '', pieces_ordered: '',
        ready_from_inventory: '', co_number: '', diamond_quality: '', gold_karat: '18kt',
        certification_lab: '', certification_lab_other: '', cobranding: false,
        inventory_gold_weight: '', inventory_diamond_weight: '',
        order_gold_weight: '', order_diamond_weight: '', chains_pieces_factory: '',
        chain_breakdown: [],
        priority: 'Normal', notes: '',
        is_stock_order: false,
      });
      setDeliveryDate(null);
      setUseCustomDeliveryDate(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteOrder = async (orderId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    try {
      await ordersApi.delete(orderId);
      toast.success('Order deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  const handleStickerConfirm = async (orderId) => {
    try {
      await ordersApi.updateStickerStatus(orderId, { sticker_printed: true });
      toast.success('Sticker marked as printed');
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, sticker_printed: true } : o));
    } catch (error) {
      toast.error('Failed to update sticker status');
    }
  };

  const handleGoldRateCut = async (orderId, data, file) => {
    const resp = await ordersApi.saveGoldRateCut(orderId, data);
    if (file) {
      await ordersApi.uploadGoldRateScreenshot(orderId, file);
    }
    setOrders(prev => prev.map(o => o.id === orderId ? resp.data : o));
  };

  const handleToggleArchive = async (orderId, e) => {
    e.stopPropagation();
    try {
      const res = await ordersApi.toggleArchive(orderId);
      toast.success(res.data.message);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, archived: res.data.archived } : o));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle archive');
    }
  };

  const handleReorder = async (orderId, direction) => {
    setReordering(orderId);
    try {
      await ordersApi.reorder({ order_id: orderId, direction });
      await fetchData();
    } catch (error) {
      toast.error('Failed to reorder');
    } finally {
      setReordering(null);
    }
  };

  const toggleDateSort = () => {
    setSortMode('date');
    setDateSortDir(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  const filteredOrders = useMemo(() => {
    let result = orders.filter(order => {
      // Archive filter
      if (!showArchived && order.archived) return false;
      if (showArchived && !order.archived) return false;

      // Stock-order view: exclude by default, show only stock when selected, or include all
      if (stockView === 'all' && order.is_stock_order) return false;
      if (stockView === 'stock' && !order.is_stock_order) return false;

      const matchesSearch =
        order.order_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (order.salesperson || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
      const matchesPriority = filterPriority === 'all' || order.priority === filterPriority;
      return matchesSearch && matchesStatus && matchesPriority;
    });

    if (sortMode === 'date') {
      result = [...result].sort((a, b) => {
        const da = new Date(a.created_at), db = new Date(b.created_at);
        return dateSortDir === 'desc' ? db - da : da - db;
      });
    } else {
      result = [...result].sort((a, b) => (a.sort_position || 0) - (b.sort_position || 0));
    }

    return result;
  }, [orders, searchQuery, filterStatus, filterPriority, sortMode, dateSortDir, showArchived, stockView]);

  const stockOrderCount = useMemo(() => orders.filter(o => o.is_stock_order && !o.archived).length, [orders]);

  const uniqueStatuses = [...new Set(orders.map(o => o.status))];

  const [exporting, setExporting] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await ordersApi.exportExcel();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `SiddhiJewels_Orders_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Orders exported to Excel');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleReconcile = async () => {
    if (!window.confirm('Reconcile stages across all orders? This will clamp any negative stage quantities to 0 and rebalance totals. Use only when you see negative numbers like "-2 pcs" in stage views.')) return;
    setReconciling(true);
    try {
      const res = await ordersApi.reconcileStages();
      const { fixed_count, orders: fixed } = res.data;
      if (fixed_count === 0) toast.info('All orders are clean — nothing to fix');
      else toast.success(`Reconciled ${fixed_count} order${fixed_count > 1 ? 's' : ''}: ${fixed.map(f => f.order_number).join(', ')}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reconcile failed');
    } finally {
      setReconciling(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="orders-page">
      <Header title="Orders" onRefresh={fetchData}>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} data-testid="export-excel-btn">
          {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Export
        </Button>
        {(user?.role === 'super_admin' || user?.role === 'admin') && (
          <Button variant="outline" size="sm" onClick={handleReconcile} disabled={reconciling} className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-500/40 dark:text-amber-400" data-testid="reconcile-stages-btn" title="Fix orders showing negative pieces in stages">
            {reconciling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
            Reconcile
          </Button>
        )}
        {isProduction() && (
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/20" data-testid="create-order-btn">
                <Plus className="h-4 w-4 mr-2" /> New Order
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto mx-4">
              <DialogHeader>
                <DialogTitle className="font-heading text-lg md:text-xl">Create New Order</DialogTitle>
                <DialogDescription className="text-xs md:text-sm">Fill in the details for the new production order</DialogDescription>
              </DialogHeader>
              
              <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-xs md:text-sm text-amber-800 dark:text-amber-200">
                  <strong>Important:</strong> Check inventory before placing order.
                </AlertDescription>
              </Alert>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 py-4">
                <div className="space-y-2 sm:col-span-2">
                  <Label>Customer (approved) *</Label>
                  <Popover open={customerComboOpen} onOpenChange={setCustomerComboOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between h-11 font-normal"
                        data-testid="order-customer-combobox"
                      >
                        <span className="truncate">
                          {newOrder.customer_name
                            ? `${newOrder.customer_name}${newOrder.branch ? ` — ${newOrder.branch}` : ''}`
                            : 'Select an approved customer…'}
                        </span>
                        <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-2" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search customers..." className="h-10" />
                        <CommandList>
                          <CommandEmpty>
                            {approvedCustomers.length === 0
                              ? 'No approved customers yet. Approve KYC in the Customers page.'
                              : 'No customers match your search.'}
                          </CommandEmpty>
                          <CommandGroup>
                            {approvedCustomers.map(c => (
                              <CommandItem
                                key={c.id}
                                value={`${c.company_name} ${c.contact_person_name || ''} ${c.mobile || ''}`}
                                onSelect={() => handleSelectCustomer(c)}
                                data-testid={`customer-option-${c.id}`}
                              >
                                <div className="flex flex-col gap-0.5 w-full">
                                  <span className="font-medium">{c.company_name}</span>
                                  <span className="text-[11px] text-muted-foreground">
                                    {c.contact_person_name || '—'} · {c.mobile || 'no mobile'}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="branch">Branch (optional)</Label>
                  <Input
                    id="branch"
                    data-testid="order-branch-input"
                    value={newOrder.branch}
                    onChange={(e) => setNewOrder({ ...newOrder, branch: e.target.value })}
                    placeholder="e.g. Andheri, Surat — shown next to the company name"
                  />
                </div>
                {/* Contacts for WhatsApp updates — live-synced with KYC */}
                {newOrder.customer_id && (
                  <div className="sm:col-span-2">
                    <WhatsAppRecipientPicker
                      customerId={newOrder.customer_id}
                      value={newOrder.notification_phones}
                      onChange={(next) => {
                        // Keep customer_phone in sync with the "primary" selection
                        // (or the first ticked contact if primary is not picked).
                        // This preserves backwards-compat with anything downstream
                        // that reads order.customer_phone.
                        setNewOrder(prev => {
                          const primary = next.find(p => p.phone === (approvedCustomers.find(c => c.id === prev.customer_id)?.mobile || ''));
                          return {
                            ...prev,
                            notification_phones: next,
                            customer_phone: primary?.phone || next[0]?.phone || '',
                          };
                        });
                      }}
                      testIdPrefix="new-order-recipient"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="salesperson">Salesperson</Label>
                  <Input id="salesperson" data-testid="order-salesperson-input" value={newOrder.salesperson} onChange={(e) => setNewOrder({...newOrder, salesperson: e.target.value})} placeholder="Enter salesperson name" />
                </div>
                <div className="space-y-2">
                  {(() => {
                    const piecesToFactory = (parseInt(newOrder.pieces_ordered) || 0) - (parseInt(newOrder.ready_from_inventory) || 0);
                    const isInventoryOnly = piecesToFactory <= 0 && (parseInt(newOrder.pieces_ordered) || 0) > 0;
                    return (
                      <>
                        <Label htmlFor="factory">
                          Factory {!isInventoryOnly && '*'}
                          {isInventoryOnly && (
                            <span className="ml-2 text-[10px] font-normal text-emerald-600 dark:text-emerald-400">
                              Not needed — order is fully from inventory
                            </span>
                          )}
                        </Label>
                        <Select
                          value={isInventoryOnly ? '' : newOrder.factory_id}
                          onValueChange={(value) => setNewOrder({...newOrder, factory_id: value})}
                          disabled={isInventoryOnly}
                        >
                          <SelectTrigger data-testid="order-factory-select" disabled={isInventoryOnly}>
                            <SelectValue placeholder={isInventoryOnly ? "Inventory-only order" : "Select factory"} />
                          </SelectTrigger>
                          <SelectContent>{factories.map(factory => (<SelectItem key={factory.id} value={factory.id}>{factory.name}</SelectItem>))}</SelectContent>
                        </Select>
                      </>
                    );
                  })()}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pieces">Total Pieces Ordered *</Label>
                  <Input id="pieces" data-testid="order-pieces-input" type="number" value={newOrder.pieces_ordered} onChange={(e) => setNewOrder({...newOrder, pieces_ordered: e.target.value})} placeholder="Enter total quantity" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="co_number">CO Number *</Label>
                  <Input id="co_number" data-testid="order-co-number-input" value={newOrder.co_number} onChange={(e) => setNewOrder({...newOrder, co_number: e.target.value})} placeholder="Enter CO number (required)" required />
                </div>
                
                {/* Ready from Inventory */}
                <div className="col-span-2 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" />
                    <div className="flex-1 space-y-2">
                      <Label htmlFor="ready_inventory" className="text-emerald-800 dark:text-emerald-200 font-semibold">Ready from Inventory</Label>
                      <Input id="ready_inventory" data-testid="order-ready-inventory-input" type="number" min="0" max={newOrder.pieces_ordered || 0} value={newOrder.ready_from_inventory} onChange={(e) => setNewOrder({...newOrder, ready_from_inventory: e.target.value})} placeholder="Pieces available in inventory" className="bg-white dark:bg-neutral-900" />
                      <p className="text-xs text-emerald-700 dark:text-emerald-300">
                        These pieces will NOT go to factory.
                        {newOrder.pieces_ordered && newOrder.ready_from_inventory && (
                          <span className="block mt-1 font-semibold">→ {parseInt(newOrder.pieces_ordered) - parseInt(newOrder.ready_from_inventory || 0)} pieces will go to factory</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Gold Karat *</Label>
                  <Select value={newOrder.gold_karat} onValueChange={(value) => setNewOrder({...newOrder, gold_karat: value})}>
                    <SelectTrigger data-testid="order-gold-karat-select"><SelectValue placeholder="Select karat" /></SelectTrigger>
                    <SelectContent>{GOLD_KARATS.map(k => (<SelectItem key={k} value={k}>{k}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Diamond Quality</Label>
                  <Select value={newOrder.diamond_quality} onValueChange={(value) => setNewOrder({...newOrder, diamond_quality: value})}>
                    <SelectTrigger data-testid="order-diamond-quality-select"><SelectValue placeholder="Select quality" /></SelectTrigger>
                    <SelectContent>{DIAMOND_QUALITIES.map(q => (<SelectItem key={q} value={q}>{q}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Certification Lab</Label>
                  <Select value={newOrder.certification_lab} onValueChange={(value) => setNewOrder({...newOrder, certification_lab: value, certification_lab_other: value !== 'Other' ? '' : newOrder.certification_lab_other})}>
                    <SelectTrigger data-testid="order-cert-lab-select"><SelectValue placeholder="Select lab" /></SelectTrigger>
                    <SelectContent>{CERTIFICATION_LABS.map(lab => (<SelectItem key={lab} value={lab}>{lab}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                {newOrder.certification_lab === 'Other' && (
                  <div className="space-y-2">
                    <Label>Certification Lab Name *</Label>
                    <Input data-testid="order-cert-lab-other-input" value={newOrder.certification_lab_other} onChange={(e) => setNewOrder({...newOrder, certification_lab_other: e.target.value})} placeholder="Enter lab name" />
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <input type="checkbox" data-testid="order-cobranding-checkbox" checked={newOrder.cobranding} onChange={(e) => setNewOrder({...newOrder, cobranding: e.target.checked})} className="h-4 w-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500" />
                    <span>Cobranding on Certification</span>
                  </Label>
                </div>

                {/* Stock Order toggle — internal stock, hidden from AR */}
                <div className="space-y-1 p-3 rounded-lg border border-dashed border-indigo-300 dark:border-indigo-500/40 bg-indigo-50/40 dark:bg-indigo-500/5">
                  <Label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="order-is-stock-checkbox"
                      checked={newOrder.is_stock_order}
                      onChange={(e) => setNewOrder({...newOrder, is_stock_order: e.target.checked})}
                      className="h-4 w-4 mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div>
                      <span className="font-medium">Stock Order (internal, not for customer)</span>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Made for our own stock. No invoice will be raised; the order is hidden from Receivables. You can still pick a customer for templating.
                      </p>
                    </div>
                  </Label>
                </div>
                
                {/* Material Weights for Order */}
                <div className="col-span-2 p-4 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30">
                  <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-3 flex items-center gap-2">
                    <Factory className="h-4 w-4" />
                    By Order (To Factory) - {parseInt(newOrder.pieces_ordered || 0) - parseInt(newOrder.ready_from_inventory || 0)} pcs
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Gold Weight (g)</Label>
                      <Input data-testid="order-gold-weight-input" type="number" step="0.01" value={newOrder.order_gold_weight} onChange={(e) => setNewOrder({...newOrder, order_gold_weight: e.target.value})} placeholder="Gold for factory order" />
                    </div>
                    <div className="space-y-2">
                      <Label>Diamond Weight (ct)</Label>
                      <Input data-testid="order-diamond-weight-input" type="number" step="0.01" value={newOrder.order_diamond_weight} onChange={(e) => setNewOrder({...newOrder, order_diamond_weight: e.target.value})} placeholder="Diamonds for factory order" />
                    </div>
                    <div className="space-y-2">
                      <Label>Pieces with Chains</Label>
                      <Input data-testid="chains-pieces-factory-input" type="number" min="0" max={parseInt(newOrder.pieces_ordered || 0) - parseInt(newOrder.ready_from_inventory || 0)} value={newOrder.chains_pieces_factory} onChange={(e) => setNewOrder({...newOrder, chains_pieces_factory: e.target.value})} placeholder="How many pieces need chains?" />
                    </div>
                  </div>
                  {parseInt(newOrder.chains_pieces_factory || 0) > 0 && (
                    <div className="mt-3 pt-3 border-t border-amber-300/50 dark:border-amber-500/30">
                      <ChainBreakdownPicker
                        value={newOrder.chain_breakdown}
                        onChange={(v) => setNewOrder({ ...newOrder, chain_breakdown: v })}
                        karat={newOrder.gold_karat}
                        totalRequired={newOrder.chains_pieces_factory}
                      />
                    </div>
                  )}
                </div>

                {/* Inventory Material Weights */}
                {parseInt(newOrder.ready_from_inventory || 0) > 0 && (
                  <div className="col-span-2 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30">
                    <h4 className="font-semibold text-emerald-800 dark:text-emerald-200 mb-3 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" /> From Inventory - {newOrder.ready_from_inventory} pcs
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Gold Weight (g)</Label>
                        <Input data-testid="inventory-gold-weight-input" type="number" step="0.01" value={newOrder.inventory_gold_weight} onChange={(e) => setNewOrder({...newOrder, inventory_gold_weight: e.target.value})} placeholder="Gold in inventory pieces" />
                      </div>
                      <div className="space-y-2">
                        <Label>Diamond Weight (ct)</Label>
                        <Input data-testid="inventory-diamond-weight-input" type="number" step="0.01" value={newOrder.inventory_diamond_weight} onChange={(e) => setNewOrder({...newOrder, inventory_diamond_weight: e.target.value})} placeholder="Diamonds in inventory pieces" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Total Weight Summary */}
                {(newOrder.order_gold_weight || newOrder.inventory_gold_weight || newOrder.order_diamond_weight || newOrder.inventory_diamond_weight) && (
                  <div className="col-span-2 p-3 rounded-lg bg-slate-100 dark:bg-neutral-800 border">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">Total Materials:</span>
                      <span>
                        Gold: {((parseFloat(newOrder.order_gold_weight) || 0) + (parseFloat(newOrder.inventory_gold_weight) || 0)).toFixed(2)}g | 
                        Diamond: {((parseFloat(newOrder.order_diamond_weight) || 0) + (parseFloat(newOrder.inventory_diamond_weight) || 0)).toFixed(2)}ct
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Delivery Date */}
                <div className="col-span-2 p-4 rounded-lg bg-slate-50 dark:bg-neutral-900 border">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <Label className="text-base font-semibold">Delivery Date</Label>
                      <p className="text-sm text-slate-500">Default: {DEFAULT_DELIVERY_DAYS} days ({format(addDays(new Date(), DEFAULT_DELIVERY_DAYS), "PPP")})</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="custom_delivery" checked={useCustomDeliveryDate} onChange={(e) => { setUseCustomDeliveryDate(e.target.checked); if (!e.target.checked) setDeliveryDate(null); }} className="h-4 w-4 rounded border-slate-300 text-amber-500" />
                      <Label htmlFor="custom_delivery" className="text-sm cursor-pointer">Custom date</Label>
                    </div>
                  </div>
                  {useCustomDeliveryDate && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" data-testid="order-delivery-date-btn" className={cn("w-full justify-start text-left font-normal", !deliveryDate && "text-muted-foreground")}>
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {deliveryDate ? format(deliveryDate, "PPP") : "Pick a custom date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={deliveryDate} onSelect={setDeliveryDate} initialFocus disabled={(date) => date < new Date()} />
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={newOrder.priority} onValueChange={(value) => setNewOrder({...newOrder, priority: value})}>
                    <SelectTrigger data-testid="order-priority-select"><SelectValue placeholder="Select priority" /></SelectTrigger>
                    <SelectContent>{PRIORITIES.map(p => (<SelectItem key={p} value={p}>{p}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Notes</Label>
                  <Textarea data-testid="order-notes-input" value={newOrder.notes} onChange={(e) => setNewOrder({...newOrder, notes: e.target.value})} placeholder="Additional notes..." rows={3} />
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateOrder} data-testid="submit-order-btn" className="bg-amber-500 hover:bg-amber-600" disabled={creating}>
                  {creating ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</>) : 'Create Order'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </Header>

      <div className="p-4 md:p-6">
        {/* Filters */}
        <Card className="mb-4 md:mb-6">
          <CardContent className="p-3 md:p-4">
            <div className="flex flex-col gap-3 md:gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Search orders..." data-testid="orders-search-input" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
              </div>
              <div className="flex flex-wrap gap-2 md:gap-4">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="flex-1 min-w-[140px] md:w-[180px]" data-testid="filter-status-select">
                    <Filter className="h-4 w-4 mr-2 hidden sm:inline" /><SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {uniqueStatuses.map(status => (<SelectItem key={status} value={status}>{status}</SelectItem>))}
                  </SelectContent>
                </Select>
                <Select value={filterPriority} onValueChange={setFilterPriority}>
                  <SelectTrigger className="flex-1 min-w-[140px] md:w-[180px]" data-testid="filter-priority-select">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    {PRIORITIES.map(p => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                  </SelectContent>
                </Select>
                <Button
                  variant={sortMode === 'custom' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSortMode(sortMode === 'custom' ? 'date' : 'custom')}
                  className={cn("text-xs", sortMode === 'custom' && "bg-amber-500 hover:bg-amber-600 text-white")}
                  data-testid="toggle-custom-sort-btn"
                >
                  <ArrowUpDown className="h-3 w-3 mr-1" />
                  {sortMode === 'custom' ? 'Custom Order' : 'Re-arrange'}
                </Button>
                <Button
                  variant={showArchived ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setShowArchived(!showArchived)}
                  className={cn("text-xs", showArchived && "bg-slate-600 hover:bg-slate-700 text-white")}
                  data-testid="toggle-archived-btn"
                >
                  <Archive className="h-3 w-3 mr-1" />
                  {showArchived ? 'Showing Archived' : 'Show Archived'}
                </Button>
                {/* Stock-order view toggle: cycles all → stock → include */}
                <Button
                  variant={stockView !== 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStockView(stockView === 'all' ? 'stock' : stockView === 'stock' ? 'include' : 'all')}
                  className={cn(
                    'text-xs',
                    stockView === 'stock' && 'bg-indigo-600 hover:bg-indigo-700 text-white',
                    stockView === 'include' && 'bg-purple-600 hover:bg-purple-700 text-white',
                  )}
                  data-testid="stock-view-toggle"
                  title={
                    stockView === 'all' ? 'Click to show only stock orders' :
                    stockView === 'stock' ? 'Click to include stock orders alongside others' :
                    'Click to hide stock orders'
                  }
                >
                  <Boxes className="h-3 w-3 mr-1" />
                  {stockView === 'all'
                    ? `Stock${stockOrderCount ? ` (${stockOrderCount})` : ''}`
                    : stockView === 'stock'
                      ? `Stock only · ${stockOrderCount}`
                      : 'All + Stock'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Material Issuance Alerts */}
        <MaterialAlertsBanner orders={orders} navigate={navigate} />

        {/* Work In Progress Summary — global + per-factory */}
        <WIPSummary orders={orders} factories={factories} />

        {/* Orders Table */}
        {loading ? (
          <Card>
            <CardContent className="p-6">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="flex items-center gap-4 py-4 border-b last:border-0">
                  <Skeleton className="h-10 w-10 rounded" />
                  <div className="flex-1 space-y-2"><Skeleton className="h-4 w-32" /><Skeleton className="h-3 w-24" /></div>
                  <Skeleton className="h-6 w-20" />
                </div>
              ))}
            </CardContent>
          </Card>
        ) : filteredOrders.length === 0 ? (
          <Card>
            <CardContent className="p-8 md:p-12 text-center">
              <Package className="h-10 w-10 md:h-12 md:w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading text-base md:text-lg font-semibold text-slate-900 dark:text-white mb-2">No orders found</h3>
              <p className="text-sm md:text-base text-slate-500 mb-4">
                {searchQuery || filterStatus !== 'all' || filterPriority !== 'all' ? 'Try adjusting your filters' : 'Create your first order to get started'}
              </p>
              {isProduction() && !searchQuery && filterStatus === 'all' && (
                <Button onClick={() => setIsCreateOpen(true)} className="bg-amber-500 hover:bg-amber-600"><Plus className="h-4 w-4 mr-2" /> Create Order</Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {filteredOrders.map((order) => {
                const daysUntil = getActiveDaysUntil(order);
                const dispatched = isOrderDispatchedOrClosed(order);
                return (
                  <Card key={order.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/orders/${order.id}`)} data-testid={`order-card-${order.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                          <span className="font-bold text-base">{order.order_number}</span>
                          <FactoryAcceptanceBadge order={order} compact />
                          <ManufacturingFormBadge order={order} compact />
                          <DesignDepChip orderId={order.id} summary={designSummary[order.id]} />
                        </div>
                        {dispatched ? (
                          <div className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400">
                            <CalendarIcon className="h-3 w-3" /> {order.status}
                          </div>
                        ) : (
                          <div className={cn("flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full",
                            daysUntil < 0 && "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
                            daysUntil >= 0 && daysUntil <= 3 && "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400",
                            daysUntil > 3 && "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400"
                          )}>
                            <CalendarIcon className="h-3 w-3" />
                            {daysUntil < 0 ? `${Math.abs(daysUntil)}d overdue` : daysUntil === 0 ? 'Due today' : `${daysUntil}d left`}
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">{order.customer_name}{order.branch ? ` — ${order.branch}` : ''}</p>
                      <div className="text-xs mb-2"><StageBreakdown stageQuantities={order.stage_quantities} status={order.status} factorySubStages={order.factory_sub_stages} dispatches={order.dispatches} /></div>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        <MaterialCell material={(order.materials || {}).gold || {}} type="gold" unit="g" orderId={order.id} navigate={navigate} />
                        <MaterialCell material={(order.materials || {}).diamonds || {}} type="diamond" unit="ct" orderId={order.id} navigate={navigate} />
                        <MaterialCell material={(order.materials || {}).chains || {}} type="chain" unit="" orderId={order.id} navigate={navigate} />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">{order.pieces_ordered} pcs</span>
                          <StickerButton order={order} onConfirm={handleStickerConfirm} />
                          <GoldRateCutButton order={order} onSave={handleGoldRateCut} userRole={user?.role} />
                        </div>
                        <Badge className={cn(getPriorityClass(order.priority), "text-[10px]")} variant="outline">{order.priority}</Badge>
                        {order.is_stock_order && (
                          <Badge variant="outline" className="text-[10px] border-indigo-400 text-indigo-700 bg-indigo-50 dark:bg-indigo-500/15 dark:text-indigo-300">
                            <Boxes className="h-2.5 w-2.5 mr-0.5" /> STOCK
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Desktop Table View */}
            <Card className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    {sortMode === 'custom' && <TableHead className="w-[60px]">Move</TableHead>}
                    <TableHead className="cursor-pointer select-none" onClick={toggleDateSort} data-testid="sort-by-date-header">
                      <span className="flex items-center gap-1">
                        Order Date
                        <ArrowUpDown className={cn("h-3 w-3 transition-colors", sortMode === 'date' ? 'text-amber-500' : 'text-muted-foreground/40')} />
                      </span>
                    </TableHead>
                    <TableHead>CO Number</TableHead>
                    <TableHead>Customer Name</TableHead>
                    <TableHead>Total Pcs</TableHead>
                    <TableHead className="min-w-[250px]">Status / Bottleneck</TableHead>
                    <TableHead className="text-center">Gold</TableHead>
                    <TableHead className="text-center">Diamond</TableHead>
                    <TableHead className="text-center">Chain</TableHead>
                    <TableHead>Sticker</TableHead>
                    <TableHead>Rate Cut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order, idx) => {
                    const daysUntil = getActiveDaysUntil(order);
                    const dispatched = isOrderDispatchedOrClosed(order);
                    return (
                      <TableRow
                        key={order.id}
                        className={cn(
                          "cursor-pointer transition-colors",
                          order.has_open_requests
                            ? "bg-red-50/60 dark:bg-red-500/5 hover:bg-red-100/60 dark:hover:bg-red-500/10 animate-pulse-subtle"
                            : "hover:bg-slate-50 dark:hover:bg-neutral-900"
                        )}
                        onClick={() => navigate(`/orders/${order.id}`)}
                        data-testid={`order-row-${order.id}`}
                      >
                          {sortMode === 'custom' && (
                            <TableCell>
                              <div className="flex flex-col gap-0.5">
                                <Button variant="ghost" size="icon" className="h-6 w-6" disabled={reordering === order.id || idx === 0}
                                  onClick={(e) => { e.stopPropagation(); handleReorder(order.id, 'up'); }} data-testid={`reorder-up-${order.id}`}>
                                  <ArrowUp className="h-3 w-3" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-6 w-6" disabled={reordering === order.id || idx === filteredOrders.length - 1}
                                  onClick={(e) => { e.stopPropagation(); handleReorder(order.id, 'down'); }} data-testid={`reorder-down-${order.id}`}>
                                  <ArrowDown className="h-3 w-3" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                          <TableCell>
                            <div>
                              <p className="text-sm font-medium text-foreground">{formatDate(order.created_at)}</p>
                              <p className={cn("text-[10px] font-medium",
                                dispatched ? "text-muted-foreground" :
                                daysUntil < 0 ? "text-red-600 dark:text-red-400" :
                                daysUntil <= 3 ? "text-amber-600 dark:text-amber-400" :
                                "text-muted-foreground"
                              )}>
                                Due: {formatDate(order.delivery_date)}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="font-bold text-foreground">{order.order_number}</span>
                            <Badge className={cn(getPriorityClass(order.priority), "ml-2 text-[10px]")} variant="outline">{order.priority}</Badge>
                            {order.is_stock_order && (
                              <Badge variant="outline" className="ml-1 text-[10px] border-indigo-400 text-indigo-700 bg-indigo-50 dark:bg-indigo-500/15 dark:text-indigo-300" data-testid={`stock-badge-${order.id}`}>
                                <Boxes className="h-2.5 w-2.5 mr-0.5" /> STOCK
                              </Badge>
                            )}
                            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                              <FactoryAcceptanceBadge order={order} />
                              <ManufacturingFormBadge order={order} />
                              <DesignDepChip orderId={order.id} summary={designSummary[order.id]} />
                            </div>
                          </TableCell>
                          <TableCell>
                            <p className="font-medium text-foreground">{order.customer_name}{order.branch ? ` — ${order.branch}` : ''}</p>
                          </TableCell>
                          <TableCell>
                            <span className="font-bold text-foreground tabular-nums">{order.pieces_ordered}</span>
                          </TableCell>
                          <TableCell>
                            <StageBreakdown stageQuantities={order.stage_quantities} status={order.status} factorySubStages={order.factory_sub_stages} dispatches={order.dispatches} />
                            {order.has_open_requests && (
                              <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-red-600 dark:text-red-400">
                                <AlertCircle className="h-3 w-3" /> Factory concern
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <MaterialCell material={(order.materials || {}).gold || {}} type="gold" unit="g" orderId={order.id} navigate={navigate} />
                          </TableCell>
                          <TableCell className="text-center">
                            <MaterialCell material={(order.materials || {}).diamonds || {}} type="diamond" unit="ct" orderId={order.id} navigate={navigate} />
                          </TableCell>
                          <TableCell className="text-center">
                            <MaterialCell material={(order.materials || {}).chains || {}} type="chain" unit="" orderId={order.id} navigate={navigate} />
                          </TableCell>
                          <TableCell>
                            <StickerButton order={order} onConfirm={handleStickerConfirm} />
                          </TableCell>
                          <TableCell>
                            <GoldRateCutButton order={order} onSave={handleGoldRateCut} userRole={user?.role} />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {order.status === 'Dispatched' && (
                                <Button variant="ghost" size="icon" className={cn("h-7 w-7", order.archived ? "text-amber-500" : "text-slate-400")}
                                  onClick={(e) => handleToggleArchive(order.id, e)}
                                  title={order.archived ? 'Unarchive' : 'Archive'}
                                  data-testid={`archive-order-${order.id}`}
                                >
                                  {order.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); navigate(`/orders/${order.id}`); }} data-testid={`view-order-${order.id}`}>
                                <Eye className="h-4 w-4" />
                              </Button>
                              {isAdmin() && (
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={(e) => handleDeleteOrder(order.id, e)} data-testid={`delete-order-${order.id}`}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          </>
        )}
      </div>

      {/* Sliding Factory Concerns drawer + tray-arrow trigger (always visible on the right edge) */}
      <FactoryConcernsPanel />
    </div>
  );
};

export default Orders;
